import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { format } from "date-fns";
import { MapPin, Calendar, PlayCircle, CheckCircle2, Clock, Home, User, Wind, AlertTriangle, Sparkles, AlertCircle, LifeBuoy } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { apiRequest } from "@/lib/queryClient";
import { CleaningPhotoUploader, CleaningPhotoViewer, PHOTO_CATEGORIES } from "@/components/cleaning-photo-uploader";
import { useCleanerApartments } from "@/hooks/use-resources";

type LaundryItem = { name: string; qty: number };

type CleaningJob = {
  id: number;
  status: string;
  jobType: string;
  scheduledDate: string | null;
  notes: string | null;
  address: string | null;
  buildingAddress?: string | null;
  apartmentName?: string | null;
  guestName?: string | null;
  bedrooms?: number | null;
  apartmentId?: number | null;
};

const statusConfig: Record<string, { label: string; variant: "default" | "secondary" | "outline" | "destructive" }> = {
  pending:     { label: "Pending",     variant: "secondary" },
  assigned:    { label: "Assigned",    variant: "outline" },
  in_progress: { label: "In Progress", variant: "default" },
  completed:   { label: "Completed",   variant: "secondary" },
};

const jobTypeLabels: Record<string, string> = {
  checkout:   "Checkout",
  intra_stay: "Mid-Stay",
  checkin:    "Check-in",
  deep:       "Deep Clean",
  inspection: "Inspection",
};

function LaundryDialog({ jobId, apartmentId, onClose, onDone, isPending }: {
  jobId: number;
  apartmentId: number | null | undefined;
  onClose: () => void;
  onDone: (jobId: number, items: LaundryItem[]) => void;
  isPending: boolean;
}) {
  const [hasLaundry, setHasLaundry] = useState<boolean | null>(null);
  const { data: rawApartments = [] } = useCleanerApartments();
  const apartments = rawApartments as { id: number; laundryItems?: { name: string; defaultQty: number }[] | null }[];
  const configuredItems = apartments.find(a => a.id === apartmentId)?.laundryItems ?? [];
  const [quantities, setQuantities] = useState<number[]>(() => configuredItems.map(i => i.defaultQty));

  const setQty = (idx: number, val: number) =>
    setQuantities(prev => prev.map((q, i) => i === idx ? Math.max(0, val) : q));

  const takenItems: LaundryItem[] = configuredItems
    .map((item, i) => ({ name: item.name, qty: quantities[i] ?? item.defaultQty }))
    .filter(i => i.qty > 0);

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-sm">
        <DialogHeader>
          <DialogTitle>Complete Job</DialogTitle>
        </DialogHeader>
        {hasLaundry === null && (
          <div className="space-y-4">
            <p className="text-sm text-muted-foreground">Any laundry to drop off?</p>
            <div className="flex gap-2">
              <Button className="flex-1" onClick={() => setHasLaundry(true)}>
                <Wind className="w-4 h-4 mr-1.5" /> Yes, log laundry
              </Button>
              <Button variant="outline" className="flex-1" onClick={() => onDone(jobId, [])} disabled={isPending}>
                No laundry
              </Button>
            </div>
          </div>
        )}
        {hasLaundry === true && (
          <div className="space-y-3">
            {configuredItems.length === 0 ? (
              <div className="flex items-start gap-2 rounded-md border border-amber-200 bg-amber-50 px-3 py-2.5 text-sm text-amber-800">
                <AlertCircle className="w-4 h-4 mt-0.5 shrink-0" />
                No laundry items configured for this unit. Ask your admin to set them up in the apartment settings.
              </div>
            ) : (
              <>
                <p className="text-sm text-muted-foreground">Adjust quantities for what you're taking to laundry.</p>
                <div className="grid grid-cols-[1fr_80px] gap-2 text-xs font-medium text-muted-foreground px-1">
                  <span>Item</span>
                  <span className="text-center">Qty</span>
                </div>
                {configuredItems.map((item, i) => (
                  <div key={i} className="grid grid-cols-[1fr_80px] items-center gap-2 bg-muted/40 rounded-md px-3 py-2">
                    <span className="text-sm font-medium">{item.name}</span>
                    <Input
                      type="number"
                      min={0}
                      value={quantities[i] ?? item.defaultQty}
                      onChange={e => setQty(i, Number(e.target.value))}
                      className="h-8 text-center"
                    />
                  </div>
                ))}
              </>
            )}
            <div className="flex gap-2 justify-end pt-1">
              <Button variant="ghost" onClick={() => setHasLaundry(null)}>Back</Button>
              <Button
                onClick={() => onDone(jobId, takenItems)}
                disabled={(configuredItems.length > 0 && !takenItems.length) || isPending}
              >
                {isPending ? "Submitting…" : "Complete & Log"}
              </Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

function JobCard({ job, onUpdate, onComplete, uploadingPhoto, uploadingCategory, uploadingBulk, onPhotoUpload, onBulkUpload, onPhotoDelete, onReportIssue }: {
  job: CleaningJob;
  onUpdate: (id: number, status: string) => void;
  onComplete: (job: CleaningJob) => void;
  uploadingPhoto: boolean;
  uploadingCategory: string | null;
  uploadingBulk: boolean;
  onPhotoUpload: (jobId: number, category: string, file: File) => void;
  onBulkUpload: (jobId: number, files: File[]) => void;
  onPhotoDelete: (jobId: number, photoId: number) => void;
  onReportIssue: (job: CleaningJob) => void;
}) {
  const cfg = statusConfig[job.status] ?? { label: job.status, variant: "outline" as const };
  const addr = job.buildingAddress ?? job.address ?? "No address";
  const scheduledAt = job.scheduledDate ? new Date(job.scheduledDate) : null;

  return (
    <Card className="border border-border/50">
      <CardContent className="p-4 space-y-3">
        <div className="flex items-start justify-between gap-2">
          <div className="flex-1 min-w-0">
            {job.apartmentName && (
              <p className="font-medium text-sm leading-tight truncate mb-1">{job.apartmentName}</p>
            )}
            <div className="flex items-center gap-2 flex-wrap">
              <Badge variant={cfg.variant}>{cfg.label}</Badge>
              <Badge variant="outline" className="text-xs">{jobTypeLabels[job.jobType] ?? job.jobType}</Badge>
              {job.bedrooms != null && <Badge variant="outline" className="text-xs">{job.bedrooms}BR</Badge>}
            </div>
          </div>
        </div>

        <div className="space-y-1 text-sm text-muted-foreground">
          <div className="flex items-center gap-2">
            <MapPin className="w-3.5 h-3.5 shrink-0 text-blue-500" />
            <a href={`https://maps.google.com/?q=${encodeURIComponent(addr)}`} target="_blank" rel="noopener noreferrer" className="truncate text-blue-600 underline underline-offset-2 hover:text-blue-800">{addr}</a>
          </div>
          {scheduledAt && (
            <div className="flex items-center gap-2">
              <Calendar className="w-3.5 h-3.5 shrink-0" />
              <span>{format(scheduledAt, "MMM d, yyyy · h:mm a")}</span>
            </div>
          )}
          {job.guestName && (
            <div className="flex items-center gap-2">
              <User className="w-3.5 h-3.5 shrink-0" />
              <span>{job.guestName}</span>
            </div>
          )}
        </div>

        {job.notes && (
          <p className="text-sm text-muted-foreground border-t pt-2 whitespace-pre-wrap break-words" data-testid={`text-worker-job-notes-${job.id}`}>{job.notes}</p>
        )}

        {(job.status === "pending" || job.status === "assigned") && (
          <div className="flex gap-2 pt-1">
            <Button size="sm" className="flex-1" onClick={() => onUpdate(job.id, "in_progress")}>
              <PlayCircle className="w-4 h-4 mr-1.5" /> Start Job
            </Button>
          </div>
        )}

        <div className="border-t pt-2">
          <Button size="sm" variant="ghost" className="w-full text-muted-foreground" onClick={() => onReportIssue(job)}>
            <LifeBuoy className="w-3.5 h-3.5 mr-1.5" /> Report Issue
          </Button>
        </div>

        {job.status === "in_progress" && (
          <CleaningPhotoUploader
            jobId={job.id}
            apartmentId={job.apartmentId ?? null}
            categories={PHOTO_CATEGORIES}
            uploadingPhoto={uploadingPhoto}
            uploadingCategory={uploadingCategory}
            uploadingBulk={uploadingBulk}
            onUpload={onPhotoUpload}
            onBulkUpload={onBulkUpload}
            onDelete={onPhotoDelete}
            onComplete={() => onComplete(job)}
            isPending={false}
          />
        )}

        {job.status === "completed" && (
          <div className="space-y-2">
            <div className="flex items-center gap-1.5 text-xs text-green-600">
              <CheckCircle2 className="w-3.5 h-3.5" /> Completed
            </div>
            <CleaningPhotoViewer jobId={job.id} categories={PHOTO_CATEGORIES} />
          </div>
        )}
      </CardContent>
    </Card>
  );
}

export default function WorkerCleaningTab({ onReportIssue }: { onReportIssue: (job: CleaningJob) => void }) {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [filter, setFilter] = useState<"pending" | "active" | "completed">("pending");
  const [completingJob, setCompletingJob] = useState<{ id: number; apartmentId?: number | null } | null>(null);
  const [uploadingPhoto, setUploadingPhoto] = useState(false);
  const [uploadingCategory, setUploadingCategory] = useState<string | null>(null);
  const [uploadingBulk, setUploadingBulk] = useState(false);

  const handleCategoryPhotoUpload = async (jobId: number, category: string, file: File) => {
    setUploadingPhoto(true);
    setUploadingCategory(category);
    try {
      const formData = new FormData();
      formData.append("file", file);
      formData.append("category", category);
      const res = await fetch(`/api/cleaning-jobs/${jobId}/photos`, { method: "POST", body: formData });
      if (!res.ok) throw new Error("Upload failed");
      queryClient.invalidateQueries({ queryKey: ["/api/cleaning-jobs", jobId, "photos"] });
      toast({ title: "Photo uploaded" });
    } catch {
      toast({ title: "Upload failed", variant: "destructive" });
    } finally {
      setUploadingPhoto(false);
      setUploadingCategory(null);
    }
  };

  const handleBulkPhotoUpload = async (jobId: number, files: File[]) => {
    if (files.length === 0) return;
    setUploadingBulk(true);
    try {
      const formData = new FormData();
      for (const file of files) formData.append("files", file);
      formData.append("category", "all");
      const res = await fetch(`/api/cleaning-jobs/${jobId}/photos/bulk`, { method: "POST", body: formData });
      if (!res.ok) throw new Error("Bulk upload failed");
      queryClient.invalidateQueries({ queryKey: ["/api/cleaning-jobs", jobId, "photos"] });
      toast({ title: `${files.length} photo${files.length !== 1 ? "s" : ""} uploaded` });
    } catch {
      toast({ title: "Upload failed", variant: "destructive" });
    } finally {
      setUploadingBulk(false);
    }
  };

  const handlePhotoDelete = async (jobId: number, photoId: number) => {
    try {
      await fetch(`/api/cleaning-jobs/${jobId}/photos/${photoId}`, { method: "DELETE" });
      queryClient.invalidateQueries({ queryKey: ["/api/cleaning-jobs", jobId, "photos"] });
    } catch {
      toast({ title: "Failed to delete photo", variant: "destructive" });
    }
  };

  const { data: jobs = [], isLoading } = useQuery<CleaningJob[]>({
    queryKey: ["/api/cleaner/jobs"],
  });

  const updateMutation = useMutation({
    mutationFn: async ({ id, status }: { id: number; status: string }) => {
      const res = await apiRequest("PUT", `/api/cleaner/jobs/${id}`, { status });
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/cleaner/jobs"] });
      toast({ title: "Job updated" });
    },
    onError: (err: Error) => toast({ title: "Error", description: err.message, variant: "destructive" }),
  });

  const completeMutation = useMutation({
    mutationFn: async ({ jobId, laundryItems }: { jobId: number; laundryItems: LaundryItem[] }) => {
      // Complete the cleaning job
      const jobRes = await apiRequest("PUT", `/api/cleaner/jobs/${jobId}`, { status: "completed" });
      if (!jobRes.ok) throw new Error("Failed to complete job");
      // If laundry items provided, create a laundry job linked to this cleaning job
      if (laundryItems.length > 0) {
        const laundryRes = await apiRequest("POST", "/api/cleaner/laundry-jobs", {
          cleaningJobId: jobId,
          takenItems: laundryItems,
        });
        if (!laundryRes.ok) throw new Error("Failed to create laundry job");
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/cleaner/jobs"] });
      queryClient.invalidateQueries({ queryKey: ["/api/admin/laundry-jobs"] });
      queryClient.invalidateQueries({ queryKey: ["/api/laundry/jobs"] });
      setCompletingJob(null);
      toast({ title: "Job completed" });
    },
    onError: (err: Error) => toast({ title: "Error", description: err.message, variant: "destructive" }),
  });

  const now = new Date();
  const todayStr = now.toDateString();
  const tomorrowStr = new Date(now.getFullYear(), now.getMonth(), now.getDate() + 1).toDateString();

  const getJobDate = (j: CleaningJob) => j.scheduledDate ? new Date(j.scheduledDate) : null;
  const sortByTime = (arr: CleaningJob[]) =>
    [...arr].sort((a, b) => (getJobDate(a)?.getTime() ?? Infinity) - (getJobDate(b)?.getTime() ?? Infinity));

  const pendingJobs   = jobs.filter(j => j.status === "pending" || j.status === "assigned");
  const inProgress    = jobs.filter(j => j.status === "in_progress");
  const done          = jobs.filter(j => j.status === "completed");
  const viewJobs      = filter === "pending" ? pendingJobs : filter === "active" ? inProgress : done;
  const overdueJobs   = sortByTime(viewJobs.filter(j => { const d = getJobDate(j); return d && d < now && d.toDateString() !== todayStr; }));
  const todayJobs     = sortByTime(viewJobs.filter(j => { const d = getJobDate(j); return d && d.toDateString() === todayStr; }));
  const tomorrowJobs  = sortByTime(viewJobs.filter(j => { const d = getJobDate(j); return d && d.toDateString() === tomorrowStr; }));
  const upcomingJobs  = sortByTime(viewJobs.filter(j => { const d = getJobDate(j); return d && d > now && d.toDateString() !== todayStr && d.toDateString() !== tomorrowStr; }));
  const undatedJobs   = viewJobs.filter(j => !getJobDate(j));

  const renderSection = (title: string, Icon: React.ElementType, color: string, sectionJobs: CleaningJob[]) => {
    if (!sectionJobs.length) return null;
    return (
      <section className="space-y-2">
        <div className={`flex items-center gap-2 text-sm font-medium ${color}`}>
          <Icon className="w-4 h-4" />
          {title} ({sectionJobs.length})
        </div>
        {sectionJobs.map(j => (
          <JobCard
            key={j.id}
            job={j}
            onUpdate={(id, status) => updateMutation.mutate({ id, status })}
            onComplete={job => setCompletingJob({ id: job.id, apartmentId: job.apartmentId })}
            uploadingPhoto={uploadingPhoto}
            uploadingCategory={uploadingCategory}
            uploadingBulk={uploadingBulk}
            onPhotoUpload={handleCategoryPhotoUpload}
            onBulkUpload={handleBulkPhotoUpload}
            onPhotoDelete={handlePhotoDelete}
            onReportIssue={onReportIssue}
          />
        ))}
      </section>
    );
  };

  if (isLoading) return (
    <div className="space-y-3">
      {[1, 2, 3].map(i => <Skeleton key={i} className="h-36 w-full" />)}
    </div>
  );

  if (!jobs.length) return (
    <div className="flex flex-col items-center justify-center py-16 text-muted-foreground gap-3">
      <Home className="w-10 h-10 opacity-30" />
      <p className="text-sm">No cleaning jobs assigned to you.</p>
    </div>
  );

  return (
    <div className="space-y-4">
      <div className="flex gap-2">
        <Button variant={filter === "pending" ? "default" : "outline"} size="sm" className="flex-1" onClick={() => setFilter("pending")}>
          Pending ({pendingJobs.length})
        </Button>
        <Button variant={filter === "active" ? "default" : "outline"} size="sm" className="flex-1" onClick={() => setFilter("active")}>
          <Clock className="w-4 h-4 mr-1" /> Active ({inProgress.length})
        </Button>
        <Button variant={filter === "completed" ? "default" : "outline"} size="sm" className="flex-1" onClick={() => setFilter("completed")}>
          <CheckCircle2 className="w-4 h-4 mr-1" /> Done ({done.length})
        </Button>
      </div>

      {viewJobs.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-12 text-muted-foreground gap-2">
          <Home className="w-8 h-8 opacity-30" />
          <p className="text-sm">
            {filter === "pending" ? "No pending jobs." : filter === "active" ? "No jobs in progress." : "No completed jobs yet."}
          </p>
        </div>
      ) : (
        <>
          {renderSection("Overdue", AlertTriangle, "text-destructive", overdueJobs)}
          {renderSection("Today", Clock, "text-primary", todayJobs)}
          {renderSection("Tomorrow", Calendar, "text-blue-500", tomorrowJobs)}
          {renderSection("Upcoming", Sparkles, "text-muted-foreground", upcomingJobs)}
          {renderSection("Unscheduled", Clock, "text-muted-foreground", undatedJobs)}
        </>
      )}

      {completingJob !== null && (
        <LaundryDialog
          jobId={completingJob.id}
          apartmentId={completingJob.apartmentId}
          onClose={() => setCompletingJob(null)}
          onDone={(jobId, items) => completeMutation.mutate({ jobId, laundryItems: items })}
          isPending={completeMutation.isPending}
        />
      )}
    </div>
  );
}
